import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { useSelector } from "react-redux";
import classNames from "classnames";

/**
 * AddressInput — Syncfusion TextBoxComponent with Google Places Autocomplete.
 *
 * Styled with `e-outline` + floating labels for visual consistency with
 * `TextInput`, `PhoneInput`, etc.
 *
 * When a place is selected from Google autocomplete the parsed address
 * components are emitted via `onPlaceSelected` so the parent can update
 * sibling form fields (city, state, zip) through react-hook-form `setValue`
 * instead of brittle DOM manipulation.
 */
export default function AddressInput({
  placeholder = "Street Address",
  value,
  onChange,
  onPlaceSelected,
  countryIsoCode,
  error,
  disabled = false,
  className,
  floatLabelType = "Auto",
  ...props
}) {
  const textBoxRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [localValue, setLocalValue] = useState(value ?? "");

  /* Keep localValue in sync when the parent updates value externally
     (e.g. via react-hook-form setValue inside onPlaceSelected). */
  useEffect(() => {
    queueMicrotask(() => setLocalValue(value ?? ""));
  }, [value]);

  const {
    currentCompany: { countryCode },
  } = useSelector(state => state.currentUser);

  const parsedCountryCode = useMemo(() => {
    const code = countryIsoCode || countryCode;
    switch (code) {
      case "USA":
        return "us";
      case "CAN":
        return "ca";
      case "MEX":
        return "mx";
      default:
        return "us";
    }
  }, [countryIsoCode, countryCode]);

  const getSnapshot = useCallback(() => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place?.address_components?.length) return;

    let mainAddress = "";
    let city = "";
    let state = "";
    let zipCode = "";

    place.address_components.forEach(component => {
      const type = component.types[0];
      switch (type) {
        case "street_number":
          mainAddress = `${component.long_name} `;
          break;
        case "route":
          mainAddress += component.long_name;
          break;
        case "postal_code":
          zipCode = component.long_name;
          break;
        case "locality":
          city = component.long_name;
          break;
        case "sublocality_level_1":
          city = city || component.long_name;
          break;
        case "administrative_area_level_1":
          state = component.short_name;
          break;
        default:
          break;
      }
    });

    setLocalValue(mainAddress);
    onChange?.(mainAddress);
    onPlaceSelected?.({ addressLine1: mainAddress, city, state, zipCode });
  }, [onChange, onPlaceSelected]);

  /* Attach / re-attach Google Places when country changes */
  useEffect(() => {
    if (!window?.google?.maps || !textBoxRef.current) return undefined;

    const inputEl = textBoxRef.current.element;
    if (!inputEl) return undefined;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputEl, {
      componentRestrictions: { country: [parsedCountryCode] },
      fields: ["address_components"],
      types: ["address"],
    });

    autocompleteRef.current.addListener("place_changed", getSnapshot);

    /* Prevent Google from overriding the Syncfusion floating-label placeholder */
    inputEl.removeAttribute("placeholder");
    const observer = new MutationObserver(mutations => {
      mutations.forEach(m => {
        if (m.attributeName === "placeholder") inputEl.removeAttribute("placeholder");
      });
    });
    observer.observe(inputEl, { attributes: true, attributeFilter: ["placeholder"] });

    return () => {
      observer.disconnect();
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [parsedCountryCode, getSnapshot]);

  const handleInput = useCallback(
    e => {
      setLocalValue(e.value);
      onChange?.(e.value);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(e => {
    if (e.keyCode === 13) {
      e.preventDefault();
    }
  }, []);

  return (
    <div className={classNames("address-input-wrapper", className)} onKeyDown={handleKeyDown}>
      <TextBoxComponent
        ref={textBoxRef}
        placeholder={placeholder}
        floatLabelType={floatLabelType}
        value={localValue}
        input={handleInput}
        enabled={!disabled}
        cssClass={classNames("e-outline", { "e-error": !!error })}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

AddressInput.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onPlaceSelected: PropTypes.func,
  countryIsoCode: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  floatLabelType: PropTypes.oneOf(["Auto", "Always", "Never"]),
};

